import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { formatInteger } from '../../utils/formatters';
import type { RegistryComponentProps } from '../../pages/registry/componentRegistry';
import { getRegistryData } from '../../pages/registry/componentRegistry';
import { usePageDataContextOptional } from '../../contexts/PageDataContext';
import Modal from '../common/Modal/Modal';
import ScallopTables from '../ScallopTables/ScallopTables';
import './SamsTotalExploitableBiomass.css';

interface ScallopMetricRow {
  mean_exploitable_biomass_mt?: number;
  mean_F?: number;
}

interface SamsTotalExploitableBiomassData {
  scallopMetricSummary?: ScallopMetricRow[];
  totalExploitableBiomass?: number;
  rawSamsData?: unknown[];
  baselineSamsData?: unknown[];
  scallopLoading?: boolean;
  error?: string | null;
  onOpenScallopBiomassModal?: () => void;
}

function computeTotalExploitableBiomass(
  scallopMetricSummary: ScallopMetricRow[] | undefined
): number | null {
  if (!scallopMetricSummary || scallopMetricSummary.length === 0) {
    return null;
  }
  return scallopMetricSummary.reduce(
    (sum, row) => sum + (row.mean_exploitable_biomass_mt ?? 0),
    0
  );
}

export default function SamsTotalExploitableBiomass(
  props: RegistryComponentProps
): React.ReactElement {
  const pageData = usePageDataContextOptional();
  const data = getRegistryData<SamsTotalExploitableBiomassData>(props);
  const [showModal, setShowModal] = useState(false);

  const pageRawSamsData = pageData?.data?.rawSamsData as unknown[] | undefined;
  const pageBaselineSamsData = pageData?.data?.baselineSamsData as unknown[] | undefined;
  const pageScallopLoading = pageData?.data?.scallopLoading as boolean | undefined;
  const pageError = pageData?.data?.error as string | null | undefined;
  const selectedModel = pageData?.data?.selectedModel as string | undefined;

  const rawSamsData = data.rawSamsData ?? pageRawSamsData ?? [];
  const baselineSamsData = data.baselineSamsData ?? pageBaselineSamsData ?? [];
  const scallopLoading = data.scallopLoading ?? pageScallopLoading ?? false;
  const error = data.error ?? pageError ?? null;

  const totalExploitableBiomass =
    data.totalExploitableBiomass ??
    computeTotalExploitableBiomass(data.scallopMetricSummary);
  const canOpenModal = false;
  const onOpenScallopBiomassModal = data.onOpenScallopBiomassModal ?? (canOpenModal ? () => setShowModal(true) : undefined);

  const handleCloseModal = () => {
    setShowModal(false);
    document.body.style.overflow = 'auto';
  };

  useEffect(() => {
    const handler = () => setShowModal(true);
    window.addEventListener('openScallopBiomassModal', handler);
    return () => window.removeEventListener('openScallopBiomassModal', handler);
  }, []);

  return (
    <div className="sams-total-exploitable-biomass insight-card">
      {/* <div className="insight-title-row">
        <h3>SAMS Assessment Metrics</h3>
      </div> */}
      <div className="insight-subtitle-container">
        <h2 className="insight-subtitle">
          Fleet-wide assessment metrics averaged across projection period
        </h2>
      </div>
      <div className="insight-columns">
        <div
          className={`insight-column ${onOpenScallopBiomassModal ? 'clickable-column' : ''}`}
          style={{
            marginRight: 0,
            cursor: onOpenScallopBiomassModal ? 'pointer' : 'default',
          }}
          onClick={onOpenScallopBiomassModal ?? undefined}
        >
          <div className="assessment-metric-item">
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span
                className="column-header"
                style={{ textAlign: 'left', marginBottom: 0 }}
              >
                Total Exploitable Biomass
              </span>
              <span className="insight-value" style={{ textAlign: 'right' }}>
                {totalExploitableBiomass != null
                  ? `${formatInteger(totalExploitableBiomass)} mt`
                  : '---'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {showModal &&
        createPortal(
          <Modal
            title="SAMS Area Projections: Exploitable Biomass (mt)"
            onClose={handleCloseModal}
            size="extra-extra-large"
            transparentBackdrop={true}
            parameterStyle={true}
            {...({ customMaxWidth: '95vw', customMaxHeight: '90vh' } as Record<string, unknown>)}
            bodyStyle={{ padding: '20px', overflow: 'auto' }}
          >
            {scallopLoading ? (
              <p className="noscroll-loading-message">Loading scallop data...</p>
            ) : error ? (
              <p className="noscroll-error-message">{error}</p>
            ) : !rawSamsData || rawSamsData.length === 0 ? (
              <p className="noscroll-no-data-message">No scallop data available</p>
            ) : (
              <ScallopTables samsData={rawSamsData} baselineSamsData={baselineSamsData} />
            )}
          </Modal>,
          document.body
        )}
    </div>
  );
}
